import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { useFormik } from 'formik';
import jwt_decode from 'jwt-decode';

import { AddEditAuditorModal } from './ActionModals/AddEditAuditorModal';
import { DeleteAuditorConfirmationModal } from './ActionModals/DeleteAuditorConfirmationModal';
import { AuditorsTable } from './Components/AuditorsTable';
import {
  getAllAuditors,
  createAuditor,
  updateAuditor,
  deleteAuditor,
} from './Helpers/Api';
import {
  getValidationSchema,
  getInitialValues,
} from './Helpers/Config';

const AuditorManagement = () => {
  const dispatch = useDispatch();
  const [auditors, setAuditors] = useState([]);
  const [modal, setModal] = useState('');
  const [user, setUser] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  // Fetch auditors without depending on user state directly
  const fetchAuditors = useCallback(async (companyId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const { auditors } = await getAllAuditors(companyId);
      setAuditors(auditors);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch auditors.',
      });
      setAuditors([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  const formik = useFormik({
    initialValues: getInitialValues(modal, selectedItem),
    validationSchema: getValidationSchema(modal),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      console.log("CLick observed")
      if (!user) {
        Swal.fire({
          icon: 'error',
          title: 'Authentication Error',
          text: 'User not authenticated. Please log in.',
        });
        setSubmitting(false);
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const auditorData = {
          ...values,
          company_id: user?.companyId || user?.company_id || user?.id, // ensure company id is included
        };

        if (modal === 'add') {
          await createAuditor(auditorData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Auditor saved successfully!',
          });
        } else if (modal === 'edit') {
          await updateAuditor(selectedItem._id, auditorData);
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Auditor updated successfully!',
          });
        }

        const companyId = auditorData.company_id;
        await fetchAuditors(companyId);
        closeModal();
        resetForm({ values: getInitialValues('', null) });
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: error.message });
        resetForm({ values: getInitialValues('', null) });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
        setSubmitting(false);
      }
    },
  });

  const openAddModal = () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(null);
    setModal('add');
  };

  const openEditModal = (item) => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('edit');
  };

  const openDeleteModal = (item) => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }
    setSelectedItem(item);
    setModal('delete');
  };

  const closeModal = () => {
    formik.resetForm({
      values: getInitialValues('', null),
    });
    setModal('');
    setSelectedItem(null);
  };

  const handleDeleteConfirm = async () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Error',
        text: 'User not authenticated. Please log in.',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteAuditor(selectedItem._id);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Auditor deleted successfully!',
      });
      const companyId = user?.companyId || user?.company_id || user?.id;
      await fetchAuditors(companyId);
      closeModal();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.message });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Initialize user on mount
  useEffect(() => {
    const userData = getUserFromToken();
    setUser(userData);
  }, [getUserFromToken]);

  // Fetch auditors only when user changes
  useEffect(() => {
    if (user) {
      const companyId = user?.companyId || user?.company_id || user?.id;
      fetchAuditors(companyId);
    }
  }, [user, fetchAuditors]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Auditor Management</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Auditors" breadcrumbItem="Auditor Management" />

        <div className="d-flex justify-content-end mb-3">
          <button className="btn btn-primary" onClick={openAddModal}>
            Add Auditor
          </button>
        </div>

        <AuditorsTable
          user={user}
          auditors={auditors}
          onEdit={openEditModal}
          onDelete={openDeleteModal}
        />

        <AddEditAuditorModal
          user={user}
          open={modal === 'add' || modal === 'edit'}
          mode={modal}
          formik={formik}
          onClose={closeModal}
          itemId={selectedItem?._id || ''}
        />

        <DeleteAuditorConfirmationModal
          user={user}
          open={modal === 'delete'}
          onClose={closeModal}
          onDelete={handleDeleteConfirm}
          item={selectedItem}
        />
      </div>
    </div>
  );
};

export { AuditorManagement };
